import React, { useMemo } from 'react';
import {  useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';
import AlertComponent from '../../../../utility/AlertComponent';
import IAlertType from '../../../../../utils/types/props/IAlertType';
import ClientSelect from '../../../../select/ClientSelect';
import { IClient } from '../../../../../utils/types/IClient';
import { IBranche } from '../../../../../utils/types/IBranche';
import apiClient from '../../../../../services/api/apiClient';
import formatNumber from '../../../../../utils/formatters/formatNumber';
import { format, parse } from 'date-fns';
import { formatAxiosError } from '../../../../../utils/formatAxiosError';
import * as XLSX from 'xlsx';
import { CarteRow, CarteSchema } from '../../../../../utils/schemas/CarteSchema';
import { fr } from 'date-fns/locale';
import { useQuery } from '@tanstack/react-query';
import QueryErrorAlerts, { QueryErrorEntry } from '../../../../others/QueryErrorAlerts';

//#region interfaces

interface ImportCartesModalProps {
    onSuccess?: () => void;
    setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
}

//#endregion

const columnMapping: Record<string, keyof CarteRow> = {
    Numéro: 'numero',
    Type: 'type',
    Porteur: 'porteur',
    'Code Article': 'codeArticle',
    'Date Validité': 'dateValidite',
    'Date Extrême Validité': 'dateExtremeValidite',
    Branche: 'branche',
    Statut: 'statut',
    'Limite Prise': 'limitePrise',
    'Limite Mensuel GO': 'limiteMensuelGo',
    'Limite Mensuel ES': 'limiteMensuelEs',
    'Code RFID': 'rfidCode',
    'Code QR': 'codeQr',
    'Code PIN': 'codePin',
    'Livraison sans recharge': 'noLimitUpToMonthLimit',
};

const excelDateToJSDate = (excelSerial: number | null): Date | null => {
    if (excelSerial == null) return null;

    const parsed = XLSX.SSF.parse_date_code(excelSerial);
    return new Date(parsed.y, parsed.m - 1, parsed.d);
};

const ImportCartesModalContent: React.FC<ImportCartesModalProps> = ({ onSuccess, setShowModal }) => {
    const {
        data: clients = [],
        isLoading: isLoadingClients,
        isFetching: isFetchingClients,
        refetch: refetchClients,
        isError: isClientsError,
        error: clientsError,
    } = useQuery<IClient[]>({
        queryKey: ['clients'],
        queryFn: async () => {
            const res = await apiClient.get('/api/clients/getClients');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des clients' },
        staleTime: 1000 * 60 * 10, // 10 min
    });

    const {
        data: branches = [],
        refetch: refetchBranches,
        isError: isBranchesError,
        error: branchesError,
    } = useQuery<IBranche[]>({
        queryKey: ['branches'],
        queryFn: async () => {
            const res = await apiClient.get('/api/branches/getBranches');
            return res.data ?? [];
        },
        meta: { title: 'Chargement des branches' },
        staleTime: 1000 * 60 * 60, // 1h
    });

    //#region useState

    const [cartes, setCartes] = useState<CarteRow[]>([]);

    const [selectedCardCode, setSelectedCardCode] = useState<string | null>(null);

    const [submittingOperation, setSubmittingOperation] = useState<boolean>(false);

    const [systemAlerts, setSystemAlerts] = useState<{ type: IAlertType; message: string }[]>([]);
    const handleDismissAlert = (index: number) => {
        setSystemAlerts((prevAlerts) => prevAlerts.filter((_, i) => i !== index));
    };
    const addAlert = (type: IAlertType, message: string) => {
        setSystemAlerts((prevAlerts) => [...prevAlerts, { type, message }]);
    };

    //#endregion

    //#region refs

    //#endregion

    //#region methods

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const data = e.target?.result;
            if (!data) return;

            const workbook = require('xlsx').read(data, { type: 'binary' });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];

            const jsonData: Record<string, any>[] = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false, dateNF: 'dd/mm/yyyy' });

            let normalizedData: any[] = [];
            try {
                normalizedData = jsonData.map((row) => {
                    const normalized: any = {};

                    for (const key in row) {
                        const mappedKey = columnMapping[key] || key;
                        normalized[mappedKey] = row[key];
                    }

                    // ---

                    if (normalized.branche) {
                        const branche = branches.find((b) => b.nom === normalized.branche);
                        if (!branche) {
                            Swal.fire({
                                text: `Branche inconnue : ${normalized.branche}`,
                                icon: 'error',
                                confirmButtonText: 'OK',
                            });

                            throw new Error(`Branche inconnue : ${normalized.branche}`);
                        }
                    }

                    // ---

                    if (normalized.dateValidite) {
                        if (typeof normalized.dateValidite === 'number') {
                            // Cas Excel date
                            normalized.dateValidite = excelDateToJSDate(normalized.dateValidite);
                        } else if (typeof normalized.dateValidite === 'string') {
                            // Cas texte au format dd/MM/yyyy
                            normalized.dateValidite = parse(normalized.dateValidite, 'dd/MM/yyyy', new Date(), { locale: fr });
                        } else if (normalized.dateValidite instanceof Date) {
                            // Déjà un objet Date ...
                        } else {
                            normalized.dateValidite = new Date();
                        }
                    } else {
                        console.warn('dateValidite manquante, utilisation de la date actuelle');
                        normalized.dateValidite = new Date();
                    }

                    if (normalized.dateExtremeValidite) {
                        if (typeof normalized.dateExtremeValidite === 'number') {
                            // Cas Excel date
                            normalized.dateExtremeValidite = excelDateToJSDate(normalized.dateExtremeValidite);
                        } else if (typeof normalized.dateExtremeValidite === 'string') {
                            // Cas texte au format dd/MM/yyyy
                            normalized.dateExtremeValidite = parse(normalized.dateExtremeValidite, 'dd/MM/yyyy', new Date(), { locale: fr });
                        } else if (normalized.dateExtremeValidite instanceof Date) {
                            // Déjà un objet Date ...
                        } else {
                            normalized.dateExtremeValidite = new Date();
                        }
                    } else {
                        const date = new Date();
                        date.setFullYear(date.getFullYear() + 2);
                        normalized.dateExtremeValidite = date;
                    }

                    if (typeof normalized.noLimitUpToMonth === 'string') {
                        normalized.noLimitUpToMonth = normalized.noLimitUpToMonth.toLowerCase() === 'oui';
                    } else {
                        normalized.noLimitUpToMonth = Boolean(normalized.noLimitUpToMonth);
                    }

                    if (!normalized.codeQr) {
                        normalized.codeQr = normalized.numero;
                    }

                    const numericFields = ['limitePrise', 'limiteMensuelGo', 'limiteMensuelEs'];
                    for (const field of numericFields) {
                        if (normalized[field] !== undefined && normalized[field] !== null && normalized[field] !== '') {
                            const parsedValue = parseFloat(normalized[field]);
                            normalized[field] = isNaN(parsedValue) ? null : parsedValue;
                        } else {
                            normalized[field] = null;
                        }
                    }

                    return normalized;
                });
            } catch (err) {
                addAlert('danger', 'Erreur lors de la normalisation des données.');
                console.error('Erreur lors de la normalisation des données :', err);
            }

            const validCartes: CarteRow[] = [];

            normalizedData.forEach((data, index) => {
                try {
                    const parsed = CarteSchema.parse(data);
                    validCartes.push(parsed);
                } catch (err) {
                    addAlert('danger', `Erreur de validation pour la ligne ${index + 2}.`);
                    console.error(`Erreur de validation pour la ligne ${index + 2} :`, err);
                }
            });

            setCartes((prev) => [...prev, ...validCartes]);
        };

        reader.readAsBinaryString(file);
    };

    const handleDeleteCarte = (index: number) => {
        setCartes((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmitAll = async () => {
        if (!selectedCardCode) {
            Swal.fire({
                text: 'Client incorrect.',
                icon: 'error',
                confirmButtonText: 'OK',
            });

            return;
        }
        if (cartes.length === 0) {
            Swal.fire({
                text: 'Aucune carte à importer.',
                icon: 'error',
                confirmButtonText: 'OK',
            });

            return;
        }

        try {
            setSubmittingOperation(true);

            const normalizeDate = (d?: Date | null): string | null => {
                if (!d) return null;
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0'); // getMonth() est 0-based
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`; // "YYYY-MM-DD"
            };

            let carteData = cartes.map((c) => ({
                codeClient: selectedCardCode,
                numero: c.numero,
                type: c.type,
                description: c.porteur,
                idArticle: c.codeArticle === 'GO' ? 1 : c.codeArticle === 'ES' ? 2 : 0,
                dateValidite: c.dateValidite != null ? normalizeDate(c.dateValidite) : null,
                dateExtremeValidite: c.dateExtremeValidite != null ? normalizeDate(c.dateExtremeValidite) : null,
                idBranche: branches.find((b) => b.nom === c.branche)?.id || null,
                statut: c.statut,
                limitePrise: c.limitePrise || null,
                limiteMensuelGo: c.limiteMensuelGo || null,
                limiteMensuelEs: c.limiteMensuelEs || null,
                rfidCode: c.rfidCode,
                codeQr: c.codeQr,
                codePin: c.codePin,
                noLimitUpToMonthLimit: c.noLimitUpToMonthLimit ? true : false,
                accessFailedCount: null,
                lockoutEnd: null,
                isLockedPermanently: null,

                id: 0,
            }));

            const response = await apiClient.post('/api/cartes/batch', {
                cartes: carteData,
                cardCode: selectedCardCode,
            });
            if (response.data.success === true) {
                Swal.fire({
                    text: 'Opération effectuée avec succès.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    onSuccess?.();
                    setShowModal?.(false);
                });
            } else {
                Swal.fire({
                    text: response.data.message || 'Erreur lors de la création des cartes.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
            }
        } catch (err) {
            Swal.fire({
                text: formatAxiosError(err)?.message ?? 'Erreur lors de la création des cartes.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setSubmittingOperation(false);
        }
    };

    //#endregion

    // ---

    const queryErrors = useMemo<QueryErrorEntry[]>(
        () => [
            { isError: isClientsError, error: clientsError, title: 'Erreur lors du chargement des clients', refetch: refetchClients },
            { isError: isBranchesError, error: branchesError, title: 'Erreur lors du chargement des branches', refetch: refetchBranches },
        ],
        [isClientsError, clientsError, refetchClients, isBranchesError, branchesError, refetchBranches],
    );

    // ---

    return (
        <>
            <AlertComponent alerts={systemAlerts} onDismiss={handleDismissAlert} />
            <QueryErrorAlerts queries={queryErrors} />

            <div>
                <form action="#">
                    <div className="row">
                        <div className="col-md-6 form-group">
                            <label htmlFor="SelectedCardCode">Client *</label>
                            <ClientSelect clients={clients} isClientsLoading={isLoadingClients || isFetchingClients} selectedCardCode={selectedCardCode} setSelectedCardCode={setSelectedCardCode} />
                        </div>

                        <div className="col-md-6 form-group">
                            <div>Fichier de données</div>
                            <input type="file" className="form-control" accept=".xlsx, .xls" onChange={handleFileUpload} />
                        </div>
                    </div>

                    <div className="hr-text">#</div>

                    {cartes && cartes.length > 0 && (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover table-nowrap table-bordered table-striped table-sm">
                                    <thead>
                                        <tr>
                                            <th>Numéro</th>
                                            <th>Type</th>
                                            <th>Porteur</th>
                                            <th>Article</th>
                                            <th>Date Validité</th>
                                            <th>Date Extr. Validité</th>
                                            <th>Zone géo.</th>
                                            <th>Statut</th>
                                            <th>Limite Prise</th>
                                            <th>Lim. Mens. GO</th>
                                            <th>Lim. Mens. ES</th>
                                            <th>Code RFID</th>
                                            <th>Code QR</th>
                                            <th>Code PIN</th>
                                            <th>Livraison sans recharge</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cartes.map((carte, index) => (
                                            <tr key={index}>
                                                <td>{carte.numero}</td>
                                                <td>{carte.type}</td>
                                                <td>{carte.porteur}</td>
                                                <td>{carte.codeArticle ?? 'Multi-produit'}</td>
                                                <td>{carte.dateValidite && format(carte.dateValidite, 'dd/MM/yyyy')}</td>
                                                <td>{carte.dateExtremeValidite && format(carte.dateExtremeValidite, 'dd/MM/yyyy')}</td>
                                                <td>{carte.branche}</td>
                                                <td>{carte.statut}</td>
                                                <td className="text-end">{carte.limitePrise && formatNumber(carte.limitePrise)}</td>
                                                <td className="text-end">{carte.limiteMensuelGo && formatNumber(carte.limiteMensuelGo)}</td>
                                                <td className="text-end">{carte.limiteMensuelEs && formatNumber(carte.limiteMensuelEs)}</td>
                                                <td>{carte.rfidCode}</td>
                                                <td>{carte.codeQr}</td>
                                                <td>{carte.codePin}</td>
                                                <td className="text-center">{carte.noLimitUpToMonthLimit ?? 'Non'}</td>

                                                <td>
                                                    <button type="button" onClick={() => handleDeleteCarte(index)} className="btn btn-sm btn-danger">
                                                        X
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    <div className="text-end mt-3">
                        <button
                            type="button"
                            className="btn btn-success"
                            onClick={() => {
                                handleSubmitAll();
                            }}
                            disabled={submittingOperation}
                        >
                            {submittingOperation ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                                    &nbsp; Valider
                                </>
                            ) : (
                                <>Valider</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

const ImportCartesModal: React.FC<ImportCartesModalProps> = () => {
    //#region useState

    // State to control modal visibility
    const [showModal, setShowModal] = useState(false);

    // Functions to toggle the modal

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    //#endregion

    // ---

    return (
        <div>
            <Button className="btn btn-primary btn-5" onClick={handleOpenModal}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-file-spreadsheet">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                    <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
                    <path d="M8 11h8v7h-8z" />
                    <path d="M8 15h8" />
                    <path d="M11 11v7" />
                </svg>
                Import
            </Button>

            {/* Modal component */}
            <Modal show={showModal} onHide={handleCloseModal} className="fade" centered scrollable backdrop="static" size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Importation des cartes</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ImportCartesModalContent setShowModal={setShowModal} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ImportCartesModal;
